import React, { useCallback, useEffect, useRef, useState } from "react";
import { LayoutGrid, Table2 } from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useBotWorkspace } from "../../../components/admin/BotWorkspace";
import useAuth from "../../../hooks/useAuth";
import * as api from "../../../api/leadsApi";

const STATUSES = ["new", "ready", "contacted", "replied", "interested", "not_interested", "closed"];
const inputClass = "mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm";
const buttonClass = "rounded-lg bg-cyan-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50";
const secondaryClass = "rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-50";

function useResource(load, dependencies, polling = false) {
  const [value, setValue] = useState(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    let timer;
    setValue(null);
    setError("");
    const fetchResource = async () => {
      try {
        const result = await load();
        if (active) { setValue(result); setError(""); }
      } catch (err) {
        if (active) setError(api.leadError(err));
      } finally {
        if (active && polling) timer = setTimeout(fetchResource, 4000);
      }
    };
    fetchResource();
    return () => { active = false; if (timer) clearTimeout(timer); };
    // Loaders are stable per resource; dependencies list their inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, revision, polling]);
  return { value, error, refresh: useCallback(() => setRevision((n) => n + 1), []) };
}

function Notice({ error, retry }) {
  return error ? <div role="alert" className="rounded-lg bg-rose-50 p-3 text-rose-800">
    {error} {retry && <button className="underline" onClick={retry}>Retry</button>}
  </div> : null;
}

function ActionButton({ action, children, className = secondaryClass }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return <span>
    <button className={className} disabled={busy} onClick={async () => {
      setBusy(true); setError("");
      try { await action(); }
      catch (err) { setError(api.leadError(err)); }
      finally { setBusy(false); }
    }}>{busy ? "Working..." : children}</button>
    <Notice error={error} />
  </span>;
}

function Field({ label, ...props }) {
  return <label className="block text-sm font-medium text-slate-700">{label}
    <input className={inputClass} {...props} />
  </label>;
}

function Select({ label, children, ...props }) {
  return <label className="block text-sm font-medium text-slate-700">{label}
    <select className={inputClass} {...props}>{children}</select>
  </label>;
}

function Form({ action, onDone, children, label = "Save" }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return <form className="space-y-3 rounded-xl border border-slate-200 bg-white p-4" onSubmit={async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setBusy(true); setError("");
    try { await action(new FormData(form)); form.reset(); onDone?.(); }
    catch (err) { setError(api.leadError(err)); }
    finally { setBusy(false); }
  }}>
    <fieldset disabled={busy} className="space-y-3">
      {children}
      {error && <p role="alert" className="text-sm text-rose-700">{error}</p>}
      <button className={buttonClass}>{busy ? "Working..." : label}</button>
    </fieldset>
  </form>;
}

function provenance(evidence, type = "operator_provided") {
  return { source_type: type, evidence_ref: evidence, retrieved_at: new Date().toISOString() };
}

function searchGroupLabel(group) {
  return `${group.query_text} · ${group.location} · ${new Date(group.created_at).toLocaleString()}`;
}

function LeadList({ bot, can }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const [adding, setAdding] = useState(false);
  const [searchRunId, setSearchRunId] = useState("");
  const [searchGroups, setSearchGroups] = useState([]);
  const [view, setView] = useState("cards");
  const navigate = useNavigate();
  const leads = useResource(
    () => api.listLeads(bot.id, {
      q: query || undefined, status: status || undefined, offset, limit: 25,
      ...(searchRunId ? { search_run_id: searchRunId } : {}),
    }),
    [bot.id, query, status, offset, searchRunId],
  );
  useEffect(() => {
    if (leads.value) setSearchGroups(leads.value.search_groups || []);
  }, [leads.value]);
  const create = async (form) => {
    const data = Object.fromEntries(form);
    const evidence = data.evidence_ref.trim();
    const field_provenance = { name: provenance(evidence) };
    if (data.website_url) field_provenance.website_url = provenance(evidence);
    if (data.category) field_provenance.category = provenance(evidence);
    if (data.address) field_provenance.address = provenance(evidence);
    const lead = await api.createLead(bot.id, {
      name: data.name,
      website_url: data.website_url || null,
      category: data.category || null,
      address: data.address ? { formatted: data.address } : {},
      field_provenance,
    });
    navigate(`/agents/${encodeURIComponent(bot.id)}/leads/${encodeURIComponent(lead.id)}`);
  };
  const renderSearchGroups = (lead) => (lead.search_groups || []).length
    ? lead.search_groups.map((group) => (
      <button key={group.id} className="block text-left text-xs text-cyan-800 underline"
        onClick={() => { setSearchRunId(group.id); setOffset(0); }}>
        {searchGroupLabel(group)}
      </button>
    )) : <span className="text-sm text-slate-500">No search group</span>;
  return <div className="space-y-4">
    <div className="flex flex-wrap gap-3">
      <input aria-label="Search leads" placeholder="Search name, category, or website" className={`${inputClass} max-w-sm`}
        value={query} onChange={(event) => { setQuery(event.target.value); setOffset(0); }} />
      <select aria-label="Filter by status" className={`${inputClass} max-w-xs`} value={status}
        onChange={(event) => { setStatus(event.target.value); setOffset(0); }}>
        <option value="">All statuses</option>{STATUSES.map((value) => <option key={value}>{value}</option>)}
      </select>
      <select aria-label="Filter by search group" className={`${inputClass} max-w-sm`}
        value={searchRunId} onChange={(event) => { setSearchRunId(event.target.value); setOffset(0); }}>
        <option value="">All search groups</option>
        {searchGroups.map((group) => <option key={group.id} value={group.id}>{searchGroupLabel(group)}</option>)}
      </select>
      <div className="ml-auto flex items-center gap-2">
        <div role="group" aria-label="Lead view"
          className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-1">
          {[["table", "Table view", Table2], ["cards", "Card view", LayoutGrid]].map(([value, label, Icon]) => (
            <button key={value} type="button" aria-label={label} title={label} aria-pressed={view === value}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${view === value
                ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              onClick={() => setView(value)}>
              <Icon size={16} aria-hidden="true" />
            </button>
          ))}
        </div>
        {can("leads.create") && <button className={buttonClass} onClick={() => setAdding(!adding)}>Add lead</button>}
      </div>
    </div>
    {adding && <Form action={create} label="Create lead">
      <Field name="name" label="Business / lead name" required maxLength={255} />
      <Field name="category" label="Category" maxLength={120} />
      <Field name="website_url" label="Website" type="url" />
      <Field name="address" label="Business address" maxLength={500} />
      <Field name="evidence_ref" label="Source evidence / reference" required maxLength={2000}
        placeholder="Where the business provided this information or the licensed source" />
      <p className="text-xs text-slate-600">A public listing or manually copying it does not remove source restrictions.</p>
    </Form>}
    <Notice error={leads.error} retry={leads.refresh} />
    {!leads.value && !leads.error && <p role="status">Loading leads…</p>}
    {leads.value && <>
      {view === "cards" ? <div aria-label="Lead cards" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {leads.value.items.map((lead) => <article key={lead.id} className="min-w-0 space-y-3 rounded-xl border bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <h2 className="break-words font-semibold"><Link className="text-cyan-800 underline"
              to={`/agents/${encodeURIComponent(bot.id)}/leads/${encodeURIComponent(lead.id)}`}>
              {lead.name || "Unnamed lead"}
            </Link></h2>
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-xs capitalize">
              {lead.status.replaceAll("_", " ")}
            </span>
          </div>
          <p className="text-sm text-slate-600">{lead.category || "Uncategorized"}</p>
          {lead.address?.formatted && <p className="text-sm">{lead.address.formatted}</p>}
          {lead.website_url && <a className="block break-all text-sm text-cyan-800 underline"
            href={lead.website_url} target="_blank" rel="noreferrer">{lead.website_url}</a>}
          <div className="space-y-1 border-t pt-3">
            <h3 className="text-xs font-semibold text-slate-600">Search group</h3>
            {renderSearchGroups(lead)}
          </div>
          <div className="flex flex-wrap justify-between gap-2 text-xs text-slate-500">
            <span>Source: {lead.source_provider || "Manual"}</span>
            <span>Last seen: {new Date(lead.last_seen_at).toLocaleDateString()}</span>
          </div>
        </article>)}
      </div> : <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-left text-sm"><thead className="bg-slate-50"><tr>
          {["Lead", "Category", "Status", "Source", "Search group", "Last seen"].map((heading) => <th className="p-3" key={heading}>{heading}</th>)}
        </tr></thead><tbody>
          {leads.value.items.map((lead) => <tr key={lead.id} className="border-t">
            <td className="p-3"><Link className="font-semibold text-cyan-800 underline"
              to={`/agents/${encodeURIComponent(bot.id)}/leads/${encodeURIComponent(lead.id)}`}>
              {lead.name || "Unnamed lead"}
            </Link></td>
            <td className="p-3">{lead.category || "—"}</td>
            <td className="p-3 capitalize">{lead.status.replaceAll("_", " ")}</td>
            <td className="p-3">{lead.source_provider || "Manual"}</td>
            <td className="p-3">
              {renderSearchGroups(lead)}
            </td>
            <td className="p-3">{new Date(lead.last_seen_at).toLocaleDateString()}</td>
          </tr>)}
        </tbody></table>
      </div>}
      {!leads.value.items.length && <p className="rounded-xl border bg-white p-6 text-slate-500">
        No leads yet. Add one or run a permitted discovery.
      </p>}
      <div className="flex items-center gap-3 text-sm">
        <button className={secondaryClass} disabled={!offset} onClick={() => setOffset(Math.max(0, offset - 25))}>Previous</button>
        <span>{leads.value.total ? `${offset + 1}–${Math.min(leads.value.total, offset + 25)} of ${leads.value.total}` : "0 results"}</span>
        <button className={secondaryClass} disabled={offset + 25 >= leads.value.total} onClick={() => setOffset(offset + 25)}>Next</button>
      </div>
    </>}
  </div>;
}

function Connections({ bot, can }) {
  const connections = useResource(() => api.listLeadConnections(bot.id), [bot.id]);
  const refresh = connections.refresh;
  return <div className="space-y-4">
    <section className="rounded-xl border bg-amber-50 p-4 text-sm text-amber-950">
      Source approval is disabled by default. Apify processes Google Maps data but does not grant rights to collect or retain it.
      Record the tenant’s independent source-use basis and evidence before enabling a connection. No secret is stored in the browser.
    </section>
    <Notice error={connections.error} retry={refresh} />
    {can("lead_discovery.configure") && <Form action={(form) =>
      api.createLeadConnection(bot.id, {
        name: String(form.get("name")).trim(), provider_code: String(form.get("provider_code")),
      }).then(refresh)} label="Create disabled connection">
      <Select name="provider_code" label="Integration provider" defaultValue="apify_compass" required>
        <option value="apify_compass">Apify</option>
      </Select>
      <Field name="name" label="Connection name" required maxLength={120} />
      <p className="text-sm text-slate-600">Reviewed adapter: Apify Compass Google Maps Scraper.</p>
    </Form>}
    {connections.value?.map((connection) => <section key={connection.id} className="space-y-3 rounded-xl border bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="mb-2 inline-flex rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-900">
            {connection.provider_code === "apify_compass" ? "Apify" : connection.provider_code || "Unknown provider"}
          </span>
          <h3 className="font-semibold">{connection.name}</h3>
          <p className="text-sm text-slate-600">{connection.actor_id} · {connection.enabled ? "Enabled" : "Disabled"}</p>
          <p className="text-sm">{connection.credential_configured ? "Server credential configured" : "Server credential missing"}</p>
          <p className="text-xs text-slate-500">Source-use evidence: {connection.source_use_evidence_ref || "Not recorded"}</p>
        </div>
        {can("lead_discovery.configure") && connection.source_use_approved_at && connection.credential_configured
          && <button className={secondaryClass} onClick={() => api.updateLeadConnection(bot.id, connection.id, {
            enabled: !connection.enabled,
          }).then(refresh).catch((err) => window.alert(api.leadError(err)))}>
            {connection.enabled ? "Disable" : "Enable"}
          </button>}
      </div>
      {can("lead_discovery.configure") && !connection.source_use_approved_at && <Form label="Record source-use approval"
        action={(form) => api.updateLeadConnection(bot.id, connection.id, {
          source_use_evidence_ref: String(form.get("evidence_ref")).trim(),
        }).then(refresh)}>
        <Field name="evidence_ref" label="Source-use basis / approval evidence" required maxLength={2000} />
      </Form>}
    </section>)}
    {!connections.value && !connections.error && <p role="status">Loading connections…</p>}
  </div>;
}

function Discovery({ bot, can }) {
  const connections = useResource(() => api.listLeadConnections(bot.id), [bot.id]);
  const runs = useResource(() => api.listLeadSearches(bot.id), [bot.id], true);
  const [selectedRun, setSelectedRun] = useState(null);
  const submission = useRef(null);
  const [resultOffset, setResultOffset] = useState(0);
  const results = useResource(() => selectedRun
    ? api.listLeadSearchResults(bot.id, selectedRun, resultOffset) : Promise.resolve(null),
    [bot.id, selectedRun, resultOffset], Boolean(selectedRun));
  const connection = connections.value?.find((item) => item.enabled && item.source_use_approved_at);
  return <div className="space-y-4">
    <section className="rounded-xl border bg-slate-50 p-4 text-sm">
      Google Maps Scraper pricing is listed by Apify from <strong>$1.50 per 1,000 scraped places</strong>;
      actual Actor/platform costs and plan discounts vary. Every run also submits a hard USD ceiling to Apify.
      Review your rights and the provider’s terms before discovery.
    </section>
    <Notice error={connections.error || runs.error} retry={() => { connections.refresh(); runs.refresh(); }} />
    {can("lead_discovery.execute") && (connection ? <Form label="Start bounded discovery" action={async (form) => {
      const data = {
        connection_id: connection.id,
        query_text: String(form.get("query_text")).trim(),
        location: String(form.get("location")).trim(),
        max_results: Number(form.get("max_results")),
        max_cost_usd: Number(form.get("max_cost_usd")),
      };
      const fingerprint = JSON.stringify(data);
      if (submission.current?.fingerprint !== fingerprint) {
        const key = window.crypto?.randomUUID?.()
          || `lead-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        submission.current = { fingerprint, key };
      }
      const run = await api.createLeadSearch(bot.id, {
        ...data, operation_key: submission.current.key,
      });
      submission.current = null;
      setSelectedRun(run.id); setResultOffset(0);
      runs.refresh();
    }}>
      <Field name="query_text" label="Business keywords" required maxLength={160} placeholder="e.g. independent bakeries" />
      <Field name="location" label="City / area" required maxLength={160} placeholder="e.g. Pune, India" />
      <Field name="max_results" label="Maximum results (1–1000)" type="number" min="1" max="1000" defaultValue="20" required />
      <Field name="max_cost_usd" label="Maximum USD charge (hard cap; at most $25)" type="number" min="0.01" max="25" step="0.01" defaultValue="1.00" required />
      <p className="text-xs text-slate-600">A timed-out start is marked uncertain and is never automatically resubmitted.</p>
    </Form> : <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
      Create, approve, and enable a source connection in Integrations first.
    </p>)}
    <Notice error={results.error} retry={results.refresh} />
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">Discovery runs</h2>
      {runs.value?.items.map((run) => <article key={run.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-4">
        <div><p className="font-medium">{run.query_text} · {run.location}</p>
          <p className="text-sm capitalize text-slate-600">{run.status} · {run.imported_count} results processed · {run.rejected_count} skipped · cap ${run.max_cost_usd}
            {run.config_snapshot?.actor_usage_total_usd != null && ` · reported usage $${run.config_snapshot.actor_usage_total_usd}`}</p>
          {run.last_error_code && <p className="text-xs text-rose-700">Issue: {run.last_error_code}</p>}
        </div>
        <div className="flex gap-2">
          <button className={secondaryClass} onClick={() => { setSelectedRun(run.id); setResultOffset(0); }}>View results</button>
          {can("lead_discovery.execute") && ["queued", "starting", "running", "importing"].includes(run.status)
            && <ActionButton action={() => api.cancelLeadSearch(bot.id, run.id).then(runs.refresh)}>Cancel</ActionButton>}
        </div>
      </article>)}
      {runs.value && !runs.value.items.length && <p className="text-sm text-slate-600">No runs yet.</p>}
    </section>
    {results.value && <section className="space-y-2 rounded-xl border bg-white p-4">
      <div className="flex items-center justify-between"><h3 className="font-semibold">Run results</h3>
        <button className={secondaryClass} onClick={() => setSelectedRun(null)}>Close</button></div>
      {results.value.items.map((lead) => <p key={lead.id} className="border-t py-2">
        {lead.name || "Unnamed"} · {lead.category || "Uncategorized"} {lead.website_url && <span>· {lead.website_url}</span>}
        {lead.address?.formatted && <span className="block text-sm">{lead.address.formatted}</span>}
        {can("leads.read") && <Link className="block text-cyan-800 underline"
          to={`/agents/${encodeURIComponent(bot.id)}/leads/${encodeURIComponent(lead.id)}`}>Open lead and contacts</Link>}
      </p>)}
      {!results.value.items.length && <p className="text-sm text-slate-600">No imported results yet.</p>}
      <div className="flex gap-3 text-sm">
        <button className={secondaryClass} disabled={!resultOffset}
          onClick={() => setResultOffset(Math.max(0, resultOffset - 50))}>Previous results</button>
        <span>{results.value.total ?? results.value.items.length} results</span>
        <button className={secondaryClass}
          disabled={resultOffset + 50 >= (results.value.total ?? results.value.items.length)}
          onClick={() => setResultOffset(resultOffset + 50)}>Next results</button>
      </div>
    </section>}
  </div>;
}

function ContactCard({ bot, lead, contact, can }) {
  const permissions = useResource(() => api.getLeadPermissions(bot.id, lead.id, contact.id),
    [bot.id, lead.id, contact.id]);
  const suppressions = useResource(() => api.listLeadSuppressions(bot.id, lead.id, contact.id),
    [bot.id, lead.id, contact.id]);
  const channel = contact.kind === "email" ? "email" : "whatsapp";
  const refresh = () => { permissions.refresh(); suppressions.refresh(); };
  return <article className="space-y-2 rounded-lg border p-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p><strong>{contact.kind}</strong>: {contact.normalized_value} · {contact.verification_status}</p>
      <details><summary className="cursor-pointer text-sm text-cyan-800">Evidence</summary>
        <pre className="max-w-xl overflow-auto whitespace-pre-wrap text-xs">{JSON.stringify(contact.provenance, null, 2)}</pre>
      </details>
    </div>
    <Notice error={permissions.error || suppressions.error} />
    {permissions.value?.map((event) => <p className="text-xs text-slate-600" key={event.id}>
      {event.channel}: {event.decision} · {event.basis} · {event.evidence_ref}
    </p>)}
    {suppressions.value?.map((item) => <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 text-sm text-rose-700">
      <p>Suppressed: {item.reason}. This block applies across the bot.</p>
      {can("lead_suppressions.manage") && <Form label="Release with evidence" action={(form) =>
        api.releaseLeadSuppression(bot.id, lead.id, contact.id, item.id, {
          reason: item.reason, evidence_ref: String(form.get("evidence_ref")).trim(),
        }).then(refresh)}>
        <Field name="evidence_ref" label="Release evidence" required maxLength={2000} />
      </Form>}
    </div>)}
    {can("leads.update") && <details><summary className="cursor-pointer text-sm">Record permission evidence</summary>
      <Form label="Append permission decision" action={(form) => api.addLeadPermission(bot.id, lead.id, contact.id, {
        channel, decision: String(form.get("decision")), basis: String(form.get("basis")).trim(),
        evidence_ref: String(form.get("evidence_ref")).trim(),
      }).then(refresh)}>
        <Select label="Decision" name="decision"><option value="granted">Granted</option><option value="denied">Denied</option><option value="revoked">Revoked</option></Select>
        <Field name="basis" label="Permission basis" required maxLength={120} placeholder="Explicit, documented opt-in" />
        <Field name="evidence_ref" label="Consent evidence reference" required maxLength={2000} />
      </Form>
    </details>}
    {can("lead_suppressions.manage") && !(suppressions.value || []).length && <details>
      <summary className="cursor-pointer text-sm text-rose-700">Suppress this destination</summary>
      <Form label="Add suppression" action={(form) => api.suppressLeadContact(bot.id, lead.id, contact.id, {
        reason: String(form.get("reason")).trim(), evidence_ref: String(form.get("evidence_ref")).trim(),
      }).then(refresh)}>
        <Field name="reason" label="Suppression reason" required maxLength={120} />
        <Field name="evidence_ref" label="Suppression evidence" required maxLength={2000} />
      </Form>
    </details>}
  </article>;
}

function LeadDetail({ bot, leadId, can }) {
  const navigate = useNavigate();
  const detail = useResource(() => api.getLead(bot.id, leadId), [bot.id, leadId]);
  const events = useResource(() => api.listLeadEvents(bot.id, leadId), [bot.id, leadId]);
  const outreachRead = can("lead_outreach.read");
  const sequences = useResource(() => outreachRead ? api.listLeadSequences(bot.id) : Promise.resolve([]),
    [bot.id, outreachRead]);
  const enrollments = useResource(() => outreachRead ? api.listLeadEnrollments(bot.id, leadId) : Promise.resolve([]),
    [bot.id, leadId, outreachRead]);
  const [preview, setPreview] = useState(null);
  const [noteError, setNoteError] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const lead = detail.value;
  const refresh = () => { detail.refresh(); events.refresh(); enrollments.refresh(); };
  const updateStatus = (form) => {
    const value = String(form.get("status"));
    return api.updateLead(bot.id, leadId, {
      status: value,
      field_provenance: { status: provenance("Operator review") },
    }).then(refresh);
  };
  return <div className="space-y-5">
    <Link to={`/agents/${encodeURIComponent(bot.id)}/leads`} className="text-cyan-800 underline">Back to leads</Link>
    <Notice error={detail.error} retry={detail.refresh} />
    {!lead && !detail.error && <p role="status">Loading lead…</p>}
    {lead && <>
      <section className="space-y-3 rounded-xl border bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div><h2 className="text-xl font-semibold">{lead.name}</h2>
            <p className="capitalize text-slate-600">{lead.status.replaceAll("_", " ")} · review {lead.review_status}</p>
          </div>
          {can("leads.update") && <Form label="Update status" action={updateStatus}>
            <Select name="status" label="Lead status" defaultValue={lead.status}>
              {STATUSES.map((value) => <option key={value}>{value}</option>)}
            </Select>
          </Form>}
          {can("leads.delete") && <ActionButton action={async () => {
            if (window.confirm("Archive this lead?")) {
              await api.archiveLead(bot.id, leadId);
              navigate(`/agents/${encodeURIComponent(bot.id)}/leads`);
            }
          }}>Archive lead</ActionButton>}
        </div>
        {lead.category && <p>{lead.category}</p>}
        {lead.website_url && <a className="break-all text-cyan-800 underline" href={lead.website_url} target="_blank" rel="noreferrer">{lead.website_url}</a>}
        {Object.keys(lead.address || {}).length > 0 && <p>{lead.address.formatted || JSON.stringify(lead.address)}</p>}
        <details><summary className="cursor-pointer text-sm">Source and field evidence</summary>
          <pre className="overflow-auto whitespace-pre-wrap text-xs">{JSON.stringify(lead.field_provenance, null, 2)}</pre>
        </details>
        {can("leads.update") && <details>
          <summary className="cursor-pointer text-sm text-cyan-800">Edit permitted business fields</summary>
          <Form label="Save field edits" action={(form) => {
            const evidence = String(form.get("evidence_ref")).trim();
            return api.updateLead(bot.id, leadId, {
              name: String(form.get("name")).trim(),
              category: String(form.get("category")).trim() || null,
              website_url: String(form.get("website_url")).trim() || null,
              address: String(form.get("address")).trim()
                ? { formatted: String(form.get("address")).trim() } : {},
              field_provenance: {
                name: provenance(evidence),
                category: provenance(evidence),
                website_url: provenance(evidence),
                address: provenance(evidence),
              },
            }).then(refresh);
          }}>
            <Field name="name" label="Business name" required maxLength={255} defaultValue={lead.name || ""} />
            <Field name="category" label="Category" maxLength={120} defaultValue={lead.category || ""} />
            <Field name="website_url" label="Website" type="url" defaultValue={lead.website_url || ""} />
            <Field name="address" label="Business address" maxLength={500} defaultValue={lead.address?.formatted || ""} />
            <Field name="evidence_ref" label="Evidence for these edits" required maxLength={2000} />
          </Form>
        </details>}
      </section>
      <section className="space-y-3 rounded-xl border bg-white p-5">
        <h3 className="font-semibold">Contacts</h3>
        {lead.contacts.map((contact) => <ContactCard key={contact.id} bot={bot} lead={lead} contact={contact} can={can} />)}
        {!lead.contacts.length && <p className="text-sm text-slate-600">No contact details are available.</p>}
        {can("leads.update") && <details><summary className="cursor-pointer text-cyan-800">Import a permitted contact</summary>
          <Form label="Add contact" action={(form) => {
            const evidence = String(form.get("evidence_ref")).trim();
            return api.addLeadContact(bot.id, leadId, {
              kind: String(form.get("kind")), value: String(form.get("value")).trim(),
              provenance: provenance(evidence),
            }).then(refresh);
          }}>
            <Select name="kind" label="Type"><option value="phone">Phone (E.164)</option><option value="email">Email</option></Select>
            <Field name="value" label="Phone or email" required maxLength={320} />
            <Field name="evidence_ref" label="Source / evidence reference" required maxLength={2000} />
          </Form>
        </details>}
      </section>
      {can("lead_outreach.read") && <section className="space-y-3 rounded-xl border bg-white p-5">
        <h3 className="font-semibold">Outreach sequences</h3>
        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-950">
          Dispatch and automatic follow-ups are disabled. Inbound reply/opt-out handling and delivery safeguards
          must be deployed before any message can be sent. Enrollment here is paused and never sends.
        </p>
        {sequences.value?.map((sequence) => <div key={sequence.id} className="flex flex-wrap items-center justify-between gap-2 border-t py-3">
          <div><p className="font-medium">{sequence.name} · revision {sequence.revision} · {sequence.status}</p>
            <p className="text-xs text-slate-600">{sequence.steps.length} step(s) · approved revision {sequence.approved_revision || "—"}</p></div>
          <div className="flex gap-2">
            <button className={secondaryClass} onClick={() => api.previewLeadSequence(bot.id, leadId, sequence.id)
              .then(setPreview).catch((err) => setNoteError(api.leadError(err)))}>Preview</button>
            {can("lead_outreach.manage") && sequence.approved_revision === sequence.revision
              && <button className={secondaryClass} onClick={() => api.enrollLeadSequence(bot.id, leadId, sequence.id, {
                operation_key: window.crypto?.randomUUID?.() || `enroll-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              }).then(enrollments.refresh).catch((err) => setNoteError(api.leadError(err)))}>Enroll paused</button>}
          </div>
        </div>)}
        {preview && <article className="rounded-lg border border-cyan-200 bg-cyan-50 p-3">
          <div className="flex justify-between"><strong>Preview only · revision {preview.revision}</strong>
            <button className="underline" onClick={() => setPreview(null)}>Close</button></div>
          {preview.steps.map((step) => <div key={step.key} className="mt-3 border-t border-cyan-200 pt-2">
            <p className="text-sm">{step.key} · {step.channel} · delay {step.delay_hours}h</p>
            {step.subject && <p className="font-medium">{step.subject}</p>}
            <p className="whitespace-pre-wrap">{step.body}</p>
          </div>)}
        </article>}
        {enrollments.value?.map((item) => <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
          <p className="text-sm">Enrollment {item.status} · sequence revision {item.sequence_revision}</p>
          {can("lead_outreach.manage") && item.status === "active" && <ActionButton
            action={() => api.updateLeadEnrollment(bot.id, item.id, { action: "pause" }).then(enrollments.refresh)}>
            Pause enrollment
          </ActionButton>}
          {can("lead_outreach.manage") && !["stopped", "completed"].includes(item.status)
            && <ActionButton action={() => api.updateLeadEnrollment(bot.id, item.id, {
              action: "stop", reason: "stopped_by_operator",
            }).then(enrollments.refresh)}>Stop enrollment</ActionButton>}
        </div>)}
      </section>}
      <section className="space-y-3 rounded-xl border bg-white p-5">
        <h3 className="font-semibold">Timeline</h3>
        {can("leads.update") && <Form label="Add note" action={async (form) => {
          setNoteBusy(true); setNoteError("");
          try { await api.addLeadNote(bot.id, leadId, { note: String(form.get("note")).trim() }); refresh(); }
          catch (err) { setNoteError(api.leadError(err)); throw err; }
          finally { setNoteBusy(false); }
        }}>
          <label className="block text-sm font-medium">Note<textarea className={inputClass} name="note" maxLength={2000} required /></label>
        </Form>}
        {noteBusy && <p role="status">Saving note…</p>}
        {noteError && <p role="alert" className="text-rose-700">{noteError}</p>}
        <Notice error={events.error} retry={events.refresh} />
        {events.value?.items.map((event) => <article key={event.id} className="border-t py-2">
          <p className="text-xs text-slate-500">{new Date(event.occurred_at).toLocaleString()} · {event.event_type}</p>
          <p className="whitespace-pre-wrap text-sm">{event.details?.note || JSON.stringify(event.details)}</p>
        </article>)}
      </section>
    </>}
  </div>;
}

function Outreach({ bot, can }) {
  const sequences = useResource(() => api.listLeadSequences(bot.id), [bot.id]);
  const refresh = sequences.refresh;
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  return <div className="space-y-4">
    <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-950">
      Sequences can be drafted, reviewed, approved, previewed, and enrolled in a paused state.
      Sending, automatic enrollment, and follow-up are intentionally disabled until inbound reply/opt-out
      processing, per-channel permission checks, suppression checks, and destination deduplication are live.
    </p>
    <Notice error={error || sequences.error} retry={refresh} />
    {can("lead_outreach.manage") && <Form label="Create one-step draft sequence" action={(form) => {
      const channel = String(form.get("channel"));
      return api.createLeadSequence(bot.id, {
        name: String(form.get("name")).trim(),
        steps: [{
          key: "intro", channel, delay_hours: 0,
          subject: channel === "email" ? String(form.get("subject")).trim() : undefined,
          body: String(form.get("body")).trim(),
        }],
      }).then(refresh);
    }}>
      <Field name="name" label="Sequence name" required maxLength={120} />
      <Select name="channel" label="Channel"><option value="email">Email</option><option value="whatsapp">WhatsApp</option></Select>
      <Field name="subject" label="Email subject (email only)" maxLength={200} />
      <label className="block text-sm font-medium">Message body
        <textarea className={inputClass} name="body" maxLength={4000} required rows={5}
          placeholder="Personalize with {{lead.name}}, {{lead.category}}, or {{lead.website_url}}" />
      </label>
      <p className="text-xs text-slate-600">Template previews substitute only the supported lead fields. No sender or template is configured.</p>
    </Form>}
    {sequences.value?.map((sequence) => <article key={sequence.id} className="space-y-3 rounded-xl border bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h3 className="font-semibold">{sequence.name}</h3>
          <p className="text-sm text-slate-600">Revision {sequence.revision} · {sequence.status}
            {sequence.approved_revision ? ` · approved revision ${sequence.approved_revision}` : " · not approved"}</p></div>
        {can("lead_outreach.approve") && sequence.approved_revision !== sequence.revision
          && <button className={buttonClass} onClick={() => api.approveLeadSequence(bot.id, sequence.id)
            .then(refresh).catch((err) => setError(api.leadError(err)))}>Approve revision</button>}
      </div>
      {sequence.steps.map((step) => <div key={step.key} className="border-t pt-2">
        <p className="text-sm">{step.channel} · {step.delay_hours}h · {step.key}</p>
        {step.subject && <p className="font-medium">{step.subject}</p>}
        <p className="whitespace-pre-wrap text-sm">{step.body}</p>
      </div>)}
    {can("lead_outreach.manage") && <>
      <button className={secondaryClass} onClick={() => setEditingId(editingId === sequence.id ? null : sequence.id)}>
        {editingId === sequence.id ? "Close editor" : "Edit revision"}
      </button>
      {editingId === sequence.id && <Form label="Save new sequence revision" action={async (form) => {
        let steps;
        try { steps = JSON.parse(String(form.get("steps_json"))); }
        catch { throw new Error("Steps must be valid JSON."); }
        await api.updateLeadSequence(bot.id, sequence.id, {
          name: String(form.get("name")).trim(),
          steps,
        });
        setEditingId(null);
        refresh();
      }}>
        <Field name="name" label="Sequence name" required maxLength={120} defaultValue={sequence.name} />
        <label className="block text-sm font-medium">Ordered step definitions (JSON)
          <textarea className={`${inputClass} font-mono`} name="steps_json" rows={10} required
            defaultValue={JSON.stringify(sequence.steps, null, 2)} />
        </label>
        <p className="text-xs text-slate-600">Up to 10 steps. Each needs a unique key, channel (email/whatsapp),
          delay_hours, and body; email steps also need a subject. Saving clears approval.</p>
      </Form>}
    </>}
    </article>)}
    {!sequences.value && !sequences.error && <p role="status">Loading sequences…</p>}
  </div>;
}

export default function LeadsPage() {
  const bot = useBotWorkspace();
  const { can } = useAuth();
  const { leadId } = useParams();
  const [search, setSearch] = useSearchParams();
  const requested = search.get("tab");
  const sections = [
    ["database", "Leads", can("leads.read")],
    ["discover", "Discover", can("lead_discovery.read")],
    ["integrations", "Integrations", can("lead_discovery.configure")],
    ["outreach", "Outreach", can("lead_outreach.read")],
  ].filter(([, , allowed]) => allowed);
  const tab = sections.some(([value]) => value === requested) ? requested : (sections[0]?.[0] || "");
  const setTab = (next) => setSearch({ tab: next });
  if (leadId) return <main className="mx-auto max-w-6xl space-y-5 px-4 py-6"><LeadDetail bot={bot} leadId={leadId} can={can} /></main>;
  return <main className="mx-auto max-w-6xl space-y-5 px-4 py-6">
    <header><h1 className="text-2xl font-bold">Leads</h1>
      <p className="mt-1 text-sm text-slate-600">Source-evidenced prospects and safe follow-up planning for {bot.name}.</p></header>
    <nav aria-label="Lead workspace" className="flex flex-wrap gap-2 border-b pb-3">
      {sections.map(([value, label]) => <button key={value} aria-current={tab === value ? "page" : undefined}
        className={`${secondaryClass} ${tab === value ? "border-cyan-800 bg-cyan-50 text-cyan-900" : ""}`}
        onClick={() => setTab(value)}>{label}</button>)}
    </nav>
    {tab === "database" && <LeadList bot={bot} can={can} />}
    {tab === "discover" && <Discovery bot={bot} can={can} />}
    {tab === "integrations" && <Connections bot={bot} can={can} />}
    {tab === "outreach" && <Outreach bot={bot} can={can} />}
  </main>;
}
